import React, {
  createRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";
import Footer from "component/footer/Footer";
import DisplayType from "utils/displayType";
import Banner from "component/banner/Banner";
import { ContactMenu } from "admin/type/type";
import { updateTitle } from "utils/commonUtil";
import { useLanguage } from "i18n/LanguageContext";
import { renderText } from "i18n/TranslatedText";
import ContactTabs from "./components/ContactTabs";
import bg from "../../assets/contact_bg.png";
import FaqSection from "./components/FaqSection";
import { ReactComponent as Arrow } from "../../assets/faq_arrow.svg";
import { ReactComponent as Graph7 } from "../../assets/faq_7_graph.svg";
import { ReactComponent as MGraph7 } from "../../assets/faq_7_graph_m.svg";

import { ReactComponent as Graph111 } from "../../assets/graph_11_1.svg";
import { ReactComponent as Graph112 } from "../../assets/graph_11_2.svg";
import { ReactComponent as Graph113 } from "../../assets/graph_11_3.svg";

import { ReactComponent as ScrollDown } from "../../assets/go_to_top.svg";

interface ListItemProps {
  id: string;
  text: string;
  ref?: React.RefObject<HTMLDivElement> | null;
}
const Faq = () => {
  const mode = DisplayType();
  const { t } = useLanguage();
  const [isGoTopShow, updateGoTopShow] = useState<boolean>(false);

  const faqRefs = useRef(
    Array.from({ length: 14 }, () => createRef<HTMLDivElement>())
  );

  const faqListItems: ListItemProps[] = useMemo(() => {
    const keys = [
      "q1",
      "q2",
      "q3",
      "q4",
      "q5",
      "q6",
      "q7",
      "q8",
      "q9",
      "q10",
      "q11",
      "q12",
      "q13",
      "q14",
    ];
    return keys.map((key, index) => ({
      id: `section_${index + 1}`,
      text: t("contact", `faq.items.${key}`),
      ref: faqRefs.current[index],
    }));
  }, [t]);

  const faqBodyRef = useRef<HTMLDivElement>(null);
  const ListItem = ({
    item,
    index,
  }: {
    item: ListItemProps;
    index: number;
  }) => {
    return (
      <ListItemContainer
        className="font-sub-title4"
        onClick={() => {
          if (!item.ref) return;
          if (!item.ref.current) return;
          if (!faqBodyRef.current) return;

          window.scrollTo({
            behavior: "smooth",
            top:
              faqBodyRef.current.offsetTop +
              item.ref.current.offsetTop -
              (mode === "Mobile" ? 65 : 80),
          });
        }}
      >
        <div>
          <span>{index < 10 ? `0${index}.` : `${index}.`}</span>
          <span>{item.text}</span>
        </div>
        <Arrow />
      </ListItemContainer>
    );
  };

  const scrollHandler = () => {
    const isTop = mode === "Mobile" ? 500 : 1000;
    updateGoTopShow(window.scrollY >= isTop);
  };

  useEffect(() => {
    window.addEventListener("scroll", scrollHandler);
    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  });

  useLayoutEffect(() => {
    updateTitle(t("contact", "faq.pageTitle"));
  }, []);

  return (
    <Container>
      <Banner bannerImg2048={bg} mode={mode}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: mode === "Mobile" ? "center" : "flex-start",
          }}
        >
          <div className="font-title-en" style={{ color: "var(--white)" }}>
            CONTACT
          </div>
        </div>
      </Banner>
      <ContactTabs selectedTab={ContactMenu.FAQ} />
      <FaqBodyContainer ref={faqBodyRef} className="limit__body">
        <FaqList>
          <div
            className={`faq__list__title ${
              mode === "Mobile" ? "font-sub-title2" : "font-sub-title"
            }`}
          >
            {t("contact", "faq.viewAll")}
          </div>
          <div className="faq__list">
            {faqListItems.map((item, index) => {
              return <ListItem key={item.id} item={item} index={index + 1} />;
            })}
          </div>
        </FaqList>

        <FaqSection
          ref={faqListItems[0].ref}
          sectionId={faqListItems[0].id}
          headerTitle={`01. ${faqListItems[0].text}`}
        >
          <div className="font-body3">
            {t("contact", "faq.answers.a1_intro")}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a1_sub1_title")}
            </div>
            {renderText(t("contact", "faq.answers.a1_sub1_body"), mode)}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a1_sub2_title")}
            </div>
            {renderText(t("contact", "faq.answers.a1_sub2_body"), mode)}
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[1].ref}
          sectionId={faqListItems[1].id}
          headerTitle={`02. ${faqListItems[1].text}`}
        >
          <div className="font-body3">
            {renderText(t("contact", "faq.answers.a2_intro"), mode)}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a2_sub1_title")}
            </div>
            {renderText(t("contact", "faq.answers.a2_sub1_body"), mode)}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a2_sub2_title")}
            </div>
            {renderText(t("contact", "faq.answers.a2_sub2_body"), mode)}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a2_sub3_title")}
            </div>
            {renderText(t("contact", "faq.answers.a2_sub3_body"), mode)}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a2_sub4_title")}
            </div>
            {renderText(t("contact", "faq.answers.a2_sub4_body"), mode)}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a2_sub5_title")}
            </div>
            {renderText(t("contact", "faq.answers.a2_sub5_body"), mode)}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a2_sub6_title")}
            </div>
            {t("contact", "faq.answers.a2_sub6_body")}
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[2].ref}
          sectionId={faqListItems[2].id}
          headerTitle={`03. ${faqListItems[2].text}`}
        >
          <div className="font-body3">{t("contact", "faq.answers.a3")}</div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[3].ref}
          sectionId={faqListItems[3].id}
          headerTitle={`04. ${faqListItems[3].text}`}
        >
          <div className="font-body3">
            {t("contact", "faq.answers.a4_intro")}
            <br />
            <br />
            <ul>
              <li className="font-bold-16">
                {t("contact", "faq.answers.a4_item1")}
              </li>
              <li className="font-bold-16">
                {t("contact", "faq.answers.a4_item2")}
              </li>
              <li className="font-bold-16">
                {t("contact", "faq.answers.a4_item3")}
              </li>
              <li className="font-bold-16">
                {t("contact", "faq.answers.a4_item4")}
              </li>
              <li className="font-bold-16">
                {t("contact", "faq.answers.a4_item5")}
              </li>
              <li className="font-bold-16">
                {t("contact", "faq.answers.a4_item6")}
              </li>
              <li className="font-bold-16">
                {t("contact", "faq.answers.a4_item7")}
              </li>
            </ul>
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[4].ref}
          sectionId={faqListItems[4].id}
          headerTitle={`05. ${faqListItems[4].text}`}
        >
          <div className="font-body3">
            {renderText(t("contact", "faq.answers.a5"), mode)}
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[5].ref}
          sectionId={faqListItems[5].id}
          headerTitle={`06. ${faqListItems[5].text}`}
        >
          <div className="font-body3">
            {renderText(t("contact", "faq.answers.a6_p1"))}
            <br />
            <br />
            {renderText(t("contact", "faq.answers.a6_p2"))}
            <br />
            <br />
            {renderText(t("contact", "faq.answers.a6_p3"))}
            <br />
            <br />
            {renderText(t("contact", "faq.answers.a6_p4"))}
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[6].ref}
          sectionId={faqListItems[6].id}
          headerTitle={`07. ${faqListItems[6].text}`}
        >
          <div className="font-body3">
            {renderText(t("contact", "faq.answers.a7_intro"))}
            <br />
            <br />
            {t("contact", "faq.answers.a7_date")}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a7_chart_title")}
            </div>
            {mode === "Mobile" ? (
              <MGraph7 style={{ width: "100%" }} />
            ) : (
              <Graph7 style={{ width: "100%" }} />
            )}
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[7].ref}
          sectionId={faqListItems[7].id}
          headerTitle={`08. ${faqListItems[7].text}`}
        >
          <div className="font-body3">
            {renderText(t("contact", "faq.answers.a8_intro"), mode)}
            <br />
            <br />
            <div
              className={`${mode === "Mobile" ? "font-body3" : "font-bold-16"}`}
            >
              {t("contact", "faq.answers.a8_sub1_title")}
            </div>
            {renderText(t("contact", "faq.answers.a8_sub1_body"), mode)}
            <br />
            <br />
            <div
              className={`${mode === "Mobile" ? "font-body3" : "font-bold-16"}`}
            >
              {t("contact", "faq.answers.a8_sub2_title")}
            </div>
            {renderText(t("contact", "faq.answers.a8_sub2_body"), mode)}
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[8].ref}
          sectionId={faqListItems[8].id}
          headerTitle={`09. ${faqListItems[8].text}`}
        >
          <div className="font-body3">{t("contact", "faq.answers.a9")}</div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[9].ref}
          sectionId={faqListItems[9].id}
          headerTitle={`10. ${faqListItems[9].text}`}
        >
          <div className="font-body3">
            <div className="font-bold-16">
              {t("contact", "faq.answers.a10_sub1_title")}
            </div>
            <ul>
              <li className="font-body3">
                {t("contact", "faq.answers.a10_sub1_item1")}
              </li>
              <li className="font-body3">
                {t("contact", "faq.answers.a10_sub1_item2")}
              </li>
            </ul>
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a10_sub2_title")}
            </div>
            <ul>
              <li className="font-body3">
                {t("contact", "faq.answers.a10_sub2_item1")}
              </li>
              <li className="font-body3">
                {t("contact", "faq.answers.a10_sub2_item2")}
              </li>
            </ul>
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a10_sub3_title")}
            </div>
            <ul>
              <li className="font-body3">
                {t("contact", "faq.answers.a10_sub3_item1")}
              </li>
              <li className="font-body3">
                {t("contact", "faq.answers.a10_sub3_item2")}
              </li>
              <li className="font-body3">
                {t("contact", "faq.answers.a10_sub3_item3")}
              </li>
            </ul>
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a10_sub4_title")}
            </div>
            <ul>
              <li className="font-body3">
                {t("contact", "faq.answers.a10_sub4_item1")}
              </li>
              <li className="font-body3">
                {t("contact", "faq.answers.a10_sub4_item2")}
              </li>
            </ul>
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[10].ref}
          sectionId={faqListItems[10].id}
          headerTitle={`11. ${faqListItems[10].text}`}
        >
          <div className="font-body3">
            {t("contact", "faq.answers.a11_date")}
            <br />
            <br />
            <div
              style={{
                display: "flex",
                flexDirection: mode === "Mobile" ? "column" : "row",
                gap: "40px",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <div className="font-bold-16">
                  {t("contact", "faq.answers.a11_chart1_title")}
                </div>
                <Graph111 />
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <div className="font-bold-16">
                  {t("contact", "faq.answers.a11_chart2_title")}
                </div>
                <Graph112 />
              </div>
            </div>
            <br />
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <div className="font-bold-16">
                {t("contact", "faq.answers.a11_chart3_title")}
              </div>
              <Graph113 />
            </div>
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a11_sub1_title")}
            </div>
            {renderText(t("contact", "faq.answers.a11_sub1_body"), mode)}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a11_sub2_title")}
            </div>
            {t("contact", "faq.answers.a11_sub2_body")}
            <br />
            <br />
            <div className="font-bold-16">
              {t("contact", "faq.answers.a11_sub3_title")}
            </div>
            {renderText(t("contact", "faq.answers.a11_sub3_body"), mode)}
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[11].ref}
          sectionId={faqListItems[11].id}
          headerTitle={`12. ${faqListItems[11].text}`}
        >
          <div className="font-body3">
            {renderText(t("contact", "faq.answers.a12"))}
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[12].ref}
          sectionId={faqListItems[12].id}
          headerTitle={`13. ${faqListItems[12].text}`}
        >
          <div className="font-body3">
            {renderText(t("contact", "faq.answers.a13"), mode)}
          </div>
        </FaqSection>

        <FaqSection
          ref={faqListItems[13].ref}
          sectionId={faqListItems[13].id}
          headerTitle={`14. ${faqListItems[13].text}`}
        >
          <div className="font-body3">
            {renderText(t("contact", "faq.answers.a14"))}
          </div>
        </FaqSection>

        {isGoTopShow && (
          <GoToTop>
            <div className="arrow_container">
              <ScrollDown
                onClick={() => {
                  window.scrollTo({ behavior: "smooth", top: 0 });
                }}
              />
            </div>
          </GoToTop>
        )}
      </FaqBodyContainer>
      <Footer />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: 768px) {
  }
`;
const FaqBodyContainer = styled.div`
  position: relative;
  flex-grow: 1;
  width: 100%;
  display: flex;
  padding-top: 100px;
  padding-bottom: 100px;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: 768px) {
    padding-top: 40px;
    padding-bottom: 40px;
    .news__list {
      margin-top: 40px;
    }
  }
`;

const FaqList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: 100px;
  border-bottom: 1px solid var(--grey-60);
  .faq__list__title {
    color: var(--grey-60);
    padding-bottom: 16px;
    border-bottom: 1px solid var(--grey-00);
  }
  .faq__list {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 30px;
  }
  @media only screen and (max-width: 768px) {
    padding-bottom: 40px;
  }
`;

const ListItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  color: var(--grey-50);
  transition: color 0.3s ease;
  cursor: pointer;
  :hover {
    color: var(--primary);
  }
  @media only screen and (max-width: 768px) {
    justify-content: space-between;
  }
`;

const GoToTop = styled.div`
  position: sticky;
  width: 100%;
  height: 40px;
  bottom: 40px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  .arrow_container {
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--grey-00);
    cursor: pointer;
    background-color: var(--white);
    opacity: 0.8;
    path {
      fill: var(--grey-40);
    }
    :hover {
      path {
        fill: var(--primary);
      }
    }
  }
`;

export default Faq;
